import { emit } from './bridge';
import { isIncomingEvent, type IncomingEventType } from './events';

export type ParentNavigationType = 'navigate_lobby' | 'navigate_deposit';

/** Same budget the host side uses for `navigate_back`: short enough that a
 *  host that never answers barely delays the button, long enough for a
 *  WebView bridge round-trip. */
export const PARENT_NAVIGATION_TIMEOUT_MS = 300;

const RESULT_TYPE: Record<ParentNavigationType, IncomingEventType> = {
  navigate_lobby: 'navigate_lobby_result',
  navigate_deposit: 'navigate_deposit_result',
};

function isHandled(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { handled?: unknown }).handled === true
  );
}

/**
 * Offers a lobby/cashier navigation to the host before the iframe does it
 * itself: hosts that wrap us in their own page (app WebViews) can't
 * intercept navigations inside the iframe. Resolves `true` only when the
 * host answers `<type>_result { handled: true }`; `handled: false` or no
 * answer in time resolves `false` and the caller navigates as before.
 */
export function requestParentNavigation(type: ParentNavigationType): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => done(false), PARENT_NAVIGATION_TIMEOUT_MS);

    function done(handled: boolean) {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(handled);
    }

    function onMessage(e: MessageEvent) {
      if (!isIncomingEvent(e.data) || e.data.type !== RESULT_TYPE[type]) return;
      done(isHandled(e.data.payload));
    }

    window.addEventListener('message', onMessage);
    emit(type);
  });
}
